import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QRCodeGenerator from 'qrcode';
import {
  ArrowLeft,
  BarChart3,
  CheckCircle2,
  Copy,
  Download,
  Edit3,
  ExternalLink,
  Eye,
  ImageIcon,
  LayoutGrid,
  Loader2,
  Lock,
  Palette,
  Power,
  PowerOff,
  QrCode,
  ShoppingBag,
  Star,
  UtensilsCrossed,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  establishmentsApi,
  menuTemplatesApi,
  menusApi,
} from '../../api';
import PublicMenuExperience from '../../components/menu/PublicMenuExperience';
import { useAlerts } from '../../components/feedback/AlertProvider';
import { useLanguage } from '../../contexts/LanguageContext';
import resolveMediaUrl from '../../utils/resolveMediaUrl';
import translateApiError from '../../utils/translateApiError';
import '../../styles/Menus/menu-details.css';
const DEFAULT_ESTABLISHMENT_ID = 1;
const PREVIEW_PHONE_WIDTH = 390;
const PREVIEW_PHONE_HEIGHT = 760;
const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (value && typeof value === 'object') {
    return Object.values(value);
  }
  return [];
};
const normalizeTranslations = (translations) => {
  if (Array.isArray(translations)) {
    return translations.filter(Boolean);
  }
  if (translations && typeof translations === 'object') {
    return Object.entries(translations).map(
      ([locale, translation]) => {
        if (!translation || typeof translation !== 'object') {
          return {
            locale,
            name: translation ? String(translation) : '',
          };
        }
        return {
          locale: translation.locale || locale,
          ...translation,
        };
      },
    );
  }
  return [];
};
const getTranslationValue = (
  item,
  field,
  language,
  fallback = 'fr',
) => {
  if (!item) return '';
  if (item?.translations?.[language]?.[field]) {
    return item.translations[language][field];
  }
  if (item?.translations?.[fallback]?.[field]) {
    return item.translations[fallback][field];
  }
  const translations = normalizeTranslations(
    item.translations,
  );
  const selectedTranslation =
    translations.find(
      (translation) => translation.locale === language,
    ) ||
    translations.find(
      (translation) => translation.locale === fallback,
    ) ||
    translations.find(
      (translation) => translation.locale === 'en',
    ) ||
    translations.find(
      (translation) => translation.locale === 'ar',
    ) ||
    translations.find(
      (translation) => translation.locale === 'es',
    ) ||
    translations[0];
  return (
    selectedTranslation?.[field] ||
    item?.[field] ||
    ''
  );
};
const translateFallback = (
  t,
  key,
  fallback,
) => {
  const value = t(key);
  return value === key ? fallback : value;
};
const getMenuImageUrl = (menu) => {
  return resolveMediaUrl(
    menu?.image_url ||
      menu?.image_path ||
      menu?.image ||
      '',
  );
};
const getTemplateName = (
  template,
  language,
) => {
  return (
    getTranslationValue(
      template,
      'name',
      language,
    ) ||
    template?.name ||
    ''
  );
};
const getBooleanStatus = (value) => {
  return (
    value === true ||
    value === 1 ||
    value === '1' ||
    value === 'true'
  );
};
function MenuDetails() {
  const { menuId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { showAlert } = useAlerts();
  const previewStageRef = useRef(null);
  const [establishmentSlug, setEstablishmentSlug] = useState('');
  const [menu, setMenu] = useState(null);
  const [previewData, setPreviewData] =
    useState(null);
  const [shareInfo, setShareInfo] =
    useState(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] =
    useState('');
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] =
    useState(false);
  const [previewZoom, setPreviewZoom] =
    useState(0.72);
  const [isPreviewZoomManual, setIsPreviewZoomManual] =
    useState(false);
  const establishmentId =
    localStorage.getItem(
      'selected_establishment_id',
    ) || DEFAULT_ESTABLISHMENT_ID;
  useEffect(() => {
    let cancelled = false;
    setEstablishmentSlug('');
    establishmentsApi.getEstablishment(establishmentId)
      .then((response) => {
        if (!cancelled) {
          const data = response.data?.data || response.data;
          setEstablishmentSlug(data?.slug || data?.establishment?.slug || '');
        }
      })
      .catch(() => {
        // The menu or preview payload may already contain the establishment slug.
      });
    return () => { cancelled = true; };
  }, [establishmentId]);
  const activeMenu =
    menu || previewData?.menu || null;
  const establishment =
    previewData?.establishment ||
    activeMenu?.establishment ||
    {};
  const template =
    previewData?.template ||
    activeMenu?.menu_template ||
    activeMenu?.template ||
    null;
  const previewDesignSettings = useMemo(
    () => ({
      ...(template?.config || {}),
      ...(activeMenu?.design_settings || {}),
      ...(activeMenu?.menu_design_settings || {}),
    }),
    [
      template,
      activeMenu?.design_settings,
      activeMenu?.menu_design_settings,
    ],
  );
  const categories = toArray(
    previewData?.categories,
  );
  const products = toArray(
    previewData?.products,
  );
  const featuredProducts = toArray(
    previewData?.featured_products,
  );
  const isActive = getBooleanStatus(
    activeMenu?.is_active,
  );
  const isDefault = getBooleanStatus(
    activeMenu?.is_default,
  );
  const orderingEnabled = Boolean(
    activeMenu?.ordering_enabled ??
      activeMenu?.settings?.ordering_enabled ??
      establishment?.ordering_enabled ??
      false,
  );
  const translatedMenuName =
    getTranslationValue(activeMenu, 'name', language) ||
    activeMenu?.name ||
    '';
  const translatedMenuDescription =
    getTranslationValue(activeMenu, 'description', language) ||
    activeMenu?.description ||
    '';
  const menuLanguageCode = activeMenu?.default_language || 'fr';
  const menuLanguageLabel = translateFallback(
    t,
    `languages.${menuLanguageCode}`,
    menuLanguageCode.toUpperCase(),
  );
  const resolvedEstablishmentSlug =
    establishment?.slug ||
    activeMenu?.establishment_slug ||
    establishmentSlug;
  const publicMenuPath =
    resolvedEstablishmentSlug && activeMenu?.slug
      ? `/${encodeURIComponent(resolvedEstablishmentSlug)}/${encodeURIComponent(activeMenu.slug)}`
      : '';
  const publicUrl = publicMenuPath
    ? (() => {
        const sharedUrl = shareInfo?.public_url || activeMenu?.public_url;
        if (sharedUrl) {
          try {
            const candidate = new URL(sharedUrl, window.location.origin);
            if (candidate.pathname.replace(/\/$/, '') === publicMenuPath) {
              return candidate.href;
            }
          } catch {
            // Ignore an invalid or old-format shared URL.
          }
        }
        return `${window.location.origin}${publicMenuPath}`;
      })()
    : '';
  const menuImageUrl = getMenuImageUrl(
    activeMenu,
  );
  const stats = useMemo(
    () => [
      {
        key: 'categories',
        label: translateFallback(
          t,
          'menus.details.stats.categories',
          'Categories',
        ),
        value: categories.length,
        icon: LayoutGrid,
      },
      {
        key: 'products',
        label: translateFallback(
          t,
          'menus.details.stats.products',
          'Products',
        ),
        value: products.length,
        icon: UtensilsCrossed,
      },
      {
        key: 'featured',
        label: translateFallback(
          t,
          'menus.details.stats.featured',
          'Featured',
        ),
        value: featuredProducts.length,
        icon: Star,
      },
      {
        key: 'template',
        label: translateFallback(
          t,
          'menus.details.stats.template',
          'Template',
        ),
        value:
          getTemplateName(
            template,
            language,
          ) ||
          translateFallback(
            t,
            'menus.details.stats.noTemplate',
            'No template',
          ),
        icon: Palette,
      },
    ],
    [
      categories.length,
      products.length,
      featuredProducts.length,
      template,
      language,
      t,
    ],
  );
  const loadMenuDetails = async () => {
    try {
      setLoading(true);
      const [
        menuResponse,
        previewResponse,
        shareResponse,
      ] = await Promise.all([
        menusApi.getMenu(menuId),
        menuTemplatesApi.getMenuPreview(
          establishmentId,
          menuId,
        ),
        menuTemplatesApi.getMenuShare(
          establishmentId,
          menuId,
        ),
      ]);
      const currentMenu =
        menuResponse.data?.data || null;
      const currentPreview =
        previewResponse.data?.data || null;
      setMenu(currentMenu);
      setPreviewData(currentPreview);
      setShareInfo(
        shareResponse.data?.data || null,
      );
      if (currentMenu?.id) {
        localStorage.setItem(
          'selected_menu_id',
          String(currentMenu.id),
        );
      }
    } catch (error) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translateApiError(
          error,
          t,
        ),
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadMenuDetails();
  }, [menuId]);
  useEffect(() => {
    if (!isActive || !publicUrl) {
      setQrCodeDataUrl('');
      return;
    }
    QRCodeGenerator.toDataURL(publicUrl, {
      width: 520,
      margin: 1,
      errorCorrectionLevel: 'H',
    })
      .then((dataUrl) => {
        setQrCodeDataUrl(dataUrl);
      })
      .catch(() => {
        setQrCodeDataUrl('');
      });
  }, [isActive, publicUrl]);
  const handleCopyMenuLink = async () => {
    if (!isActive || !publicUrl) return;
    try {
      await navigator.clipboard.writeText(
        publicUrl,
      );
      showAlert({
        type: 'success',
        title: t('alerts.success'),
        message: translateFallback(
          t,
          'menus.details.share.copied',
          'Menu link copied successfully.',
        ),
      });
    } catch {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translateFallback(
          t,
          'menus.details.share.copyFailed',
          'Unable to copy the menu link.',
        ),
      });
    }
  };
  const handleDownloadQrCode = () => {
    if (!isActive || !qrCodeDataUrl) return;
    const link = document.createElement('a');
    link.href = qrCodeDataUrl;
    link.download = `${
      activeMenu?.slug || 'menu'
    }-qr-code.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleToggleStatus = async () => {
    if (!activeMenu?.id) return;
    const nextValue = !isActive;
    try {
      setLoadingAction(true);
      await menusApi.updateStatus(
        activeMenu.id,
        nextValue,
      );
      setMenu((currentMenu) => ({
        ...currentMenu,
        is_active: nextValue,
      }));
      setPreviewData((currentPreview) => {
        if (!currentPreview) {
          return currentPreview;
        }
        return {
          ...currentPreview,
          menu: {
            ...currentPreview.menu,
            is_active: nextValue,
          },
        };
      });
      showAlert({
        type: 'success',
        title: t('alerts.success'),
        message: nextValue
          ? translateFallback(
              t,
              'menus.details.activation.activatedMessage',
              'Menu activated successfully. The QR code is now available.',
            )
          : translateFallback(
              t,
              'menus.details.activation.deactivatedMessage',
              'Menu deactivated successfully. The QR code is now hidden.',
            ),
      });
    } catch (error) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translateApiError(
          error,
          t,
        ),
      });
    } finally {
      setLoadingAction(false);
    }
  };
  const handleToggleOrdering = async () => {
    if (!activeMenu?.id) return;
    const nextValue = !orderingEnabled;
    try {
      setLoadingAction(true);
      await menusApi.updateOrdering(
        activeMenu.id,
        nextValue,
      );
      setMenu((currentMenu) => ({
        ...currentMenu,
        ordering_enabled: nextValue,
        settings: {
          ...(currentMenu?.settings || {}),
          ordering_enabled: nextValue,
        },
      }));
      setPreviewData((currentPreview) => {
        if (!currentPreview) {
          return currentPreview;
        }
        return {
          ...currentPreview,
          menu: {
            ...currentPreview.menu,
            ordering_enabled: nextValue,
            settings: {
              ...(currentPreview.menu?.settings || {}),
              ordering_enabled: nextValue,
            },
          },
        };
      });
      showAlert({
        type: 'success',
        title: t('alerts.success'),
        message: nextValue
          ? translateFallback(
              t,
              'menus.details.ordering.activatedMessage',
              'Online ordering activated for this menu.',
            )
          : translateFallback(
              t,
              'menus.details.ordering.deactivatedMessage',
              'Online ordering deactivated for this menu.',
            ),
      });
    } catch (error) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translateApiError(
          error,
          t,
        ),
      });
    } finally {
      setLoadingAction(false);
    }
  };
  const handleSetDefault = async () => {
    if (!activeMenu?.id || isDefault) return;
    try {
      setLoadingAction(true);
      await menusApi.setDefault(
        activeMenu.id,
      );
      setMenu((currentMenu) => ({
        ...currentMenu,
        is_default: true,
      }));
      setPreviewData((currentPreview) => {
        if (!currentPreview) {
          return currentPreview;
        }
        return {
          ...currentPreview,
          menu: {
            ...currentPreview.menu,
            is_default: true,
          },
        };
      });
      showAlert({
        type: 'success',
        title: t('alerts.success'),
        message: translateFallback(
          t,
          'menus.details.default.saved',
          'This menu is now the default menu.',
        ),
      });
    } catch (error) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translateApiError(
          error,
          t,
        ),
      });
    } finally {
      setLoadingAction(false);
    }
  };
  const fitPreviewToCanvas = useCallback(() => {
    const stage = previewStageRef.current;
    if (!stage) {
      return;
    }
    const { width, height } =
      stage.getBoundingClientRect();
    if (!width || !height) {
      return;
    }
    const availableWidth =
      Math.max(width - 70, 260);
    const availableHeight =
      Math.max(height - 90, 360);
    const nextZoom = Math.min(
      availableWidth /
        PREVIEW_PHONE_WIDTH,
      availableHeight /
        PREVIEW_PHONE_HEIGHT,
      1.1,
    );
    setPreviewZoom(
      Number(
        Math.max(
          0.28,
          nextZoom,
        ).toFixed(2),
      ),
    );
  }, []);
  const scrollPhonePreview = useCallback(
    (event) => {
      const stage = previewStageRef.current;
      if (!stage) {
        return false;
      }
      const scrollRegion =
        stage.querySelector(
          '.menu-details-public-phone .public-menu--phone-preview > .public-menu__scroll-region',
        ) ||
        stage.querySelector(
          '.menu-details-public-phone .public-menu--phone-preview',
        );
      if (!scrollRegion) {
        return false;
      }
      const maxScrollTop =
        scrollRegion.scrollHeight -
        scrollRegion.clientHeight;
      if (maxScrollTop <= 0) {
        return false;
      }
      const nextScrollTop = Math.min(
        maxScrollTop,
        Math.max(
          0,
          scrollRegion.scrollTop +
            event.deltaY,
        ),
      );
      if (
        nextScrollTop ===
        scrollRegion.scrollTop
      ) {
        return false;
      }
      event.preventDefault();
      event.stopPropagation();
      scrollRegion.scrollTop =
        nextScrollTop;
      return true;
    },
    [],
  );
  useEffect(() => {
    const handleWheel = (event) => {
      const stage = previewStageRef.current;
      if (
        !stage ||
        !stage.contains(event.target)
      ) {
        return;
      }
      if (
        event.ctrlKey ||
        event.metaKey
      ) {
        event.preventDefault();
        event.stopPropagation();
        const direction =
          event.deltaY > 0 ? -1 : 1;
        setIsPreviewZoomManual(true);
        setPreviewZoom((current) =>
          Number(
            Math.min(
              1.25,
              Math.max(
                0.28,
                current +
                  direction * 0.05,
              ),
            ).toFixed(2),
          ),
        );
        return;
      }
      if (
        event.target.closest(
          '.menu-details-public-phone',
        )
      ) {
        scrollPhonePreview(event);
      }
    };
    document.addEventListener(
      'wheel',
      handleWheel,
      {
        passive: false,
        capture: true,
      },
    );
    return () =>
      document.removeEventListener(
        'wheel',
        handleWheel,
        {
          capture: true,
        },
      );
  }, [scrollPhonePreview]);
  useEffect(() => {
    const stage = previewStageRef.current;
    if (!stage) {
      return undefined;
    }
    if (!isPreviewZoomManual) {
      requestAnimationFrame(
        fitPreviewToCanvas,
      );
    }
    const observer = new ResizeObserver(() => {
      if (!isPreviewZoomManual) {
        requestAnimationFrame(
          fitPreviewToCanvas,
        );
      }
    });
    observer.observe(stage);
    return () => observer.disconnect();
  }, [
    fitPreviewToCanvas,
    isPreviewZoomManual,
    activeMenu?.id,
  ]);
  if (loading) {
    return (
      <div className="menu-details-page">
        <div className="menu-details-loading">
          <Loader2 size={36} />
          <h2>
            {translateFallback(
              t,
              'menus.details.loading',
              'Loading menu details...',
            )}
          </h2>
        </div>
      </div>
    );
  }
  if (!activeMenu) {
    return (
      <div className="menu-details-page">
        <div className="menu-details-empty">
          <UtensilsCrossed size={36} />
          <h2>
            {translateFallback(
              t,
              'menus.details.notFound',
              'Menu not found.',
            )}
          </h2>
          <Link
            to="/menus"
            className="dashboard-primary-button"
          >
            {translateFallback(
              t,
              'menus.details.backToMenus',
              'Back to menus',
            )}
          </Link>
        </div>
      </div>
    );
  }
  return (
    <div className="menu-details-page">
      <section className="menu-details-heading">
        <div className="menu-details-heading__main">
          <button
            type="button"
            className="menu-details-back-button"
            onClick={() => navigate(-1)}
            aria-label={t('common.back')}
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <p className="menu-details-heading__eyebrow">
              {translateFallback(
                t,
                'menus.details.eyebrow',
                'Menu details',
              )}
            </p>
            <h1>{translatedMenuName}</h1>
            <p>
              {translatedMenuDescription ||
                translateFallback(
                  t,
                  'menus.details.noDescription',
                  'No description added for this menu yet.',
                )}
            </p>
            <div className="menu-details-badges">
              <span
                className={
                  isActive
                    ? 'is-active'
                    : 'is-inactive'
                }
              >
                {isActive ? (
                  <CheckCircle2
                    size={14}
                  />
                ) : (
                  <PowerOff size={14} />
                )}
                {isActive
                  ? translateFallback(
                      t,
                      'menus.status.active',
                      'Active',
                    )
                  : translateFallback(
                      t,
                      'menus.status.inactive',
                      'Inactive',
                    )}
              </span>
              {isDefault && (
                <span className="is-default">
                  <Star size={14} />
                  {translateFallback(
                    t,
                    'menus.status.default',
                    'Default',
                  )}
                </span>
              )}
              <span>
                {menuLanguageLabel}
              </span>
              <span
                className={
                  orderingEnabled
                    ? 'is-active'
                    : 'is-inactive'
                }
              >
                <ShoppingBag size={14} />
                {orderingEnabled
                  ? translateFallback(
                      t,
                      'menus.details.ordering.activeBadge',
                      'Orders on',
                    )
                  : translateFallback(
                      t,
                      'menus.details.ordering.inactiveBadge',
                      'Orders off',
                    )}
              </span>
            </div>
          </div>
        </div>
        <div className="menu-details-heading__actions">
          <Link
            to={`/menus/${activeMenu.id}/edit`}
            className="dashboard-secondary-button"
          >
            <Edit3 size={17} />
            {translateFallback(
              t,
              'menus.actions.editDetails',
              'Edit details',
            )}
          </Link>
          <Link
            to={`/menus/${activeMenu.id}/customize`}
            className="dashboard-secondary-button"
          >
            <Palette size={17} />
            {translateFallback(
              t,
              'menus.actions.customize',
              'Customize',
            )}
          </Link>
          {isActive && publicUrl && (
            <Link
              to={publicMenuPath}
              target="_blank"
              rel="noreferrer"
              className="dashboard-primary-button"
            >
              <ExternalLink size={17} />
              {translateFallback(
                t,
                'menus.actions.publicPreview',
                'Public preview',
              )}
            </Link>
          )}
        </div>
      </section>
      <section className="menu-details-overview-grid">
        <article className="menu-details-info-card">
          <div className="menu-details-info-card__image">
            {menuImageUrl ? (
              <img
                src={menuImageUrl}
                alt={activeMenu.name}
              />
            ) : (
              <ImageIcon size={30} />
            )}
          </div>
          <div className="menu-details-info-card__content">
            <p>
              {translateFallback(
                t,
                'menus.details.information',
                'Menu information',
              )}
            </p>
            <h2>{translatedMenuName}</h2>
            <dl>
              <div>
                <dt>
                  {translateFallback(
                    t,
                    'menus.details.slug',
                    'Slug',
                  )}
                </dt>
                <dd>{activeMenu.slug}</dd>
              </div>
              <div>
                <dt>
                  {translateFallback(
                    t,
                    'menus.details.language',
                    'Language',
                  )}
                </dt>
                <dd>
                  {menuLanguageLabel}
                </dd>
              </div>
              <div>
                <dt>
                  {translateFallback(
                    t,
                    'menus.details.sortOrder',
                    'Sort order',
                  )}
                </dt>
                <dd>
                  {activeMenu.sort_order ?? 0}
                </dd>
              </div>
              <div>
                <dt>
                  {translateFallback(
                    t,
                    'menus.details.template',
                    'Template',
                  )}
                </dt>
                <dd>
                  {getTemplateName(
                    template,
                    language,
                  ) ||
                    translateFallback(
                      t,
                      'menus.details.noTemplate',
                      'No template',
                    )}
                </dd>
              </div>
            </dl>
          </div>
        </article>
        <div className="menu-details-status-stack">
        <article
          className={`menu-details-activation-card ${
            isActive
              ? 'is-active'
              : 'is-inactive'
          }`}
        >
          <div className="menu-details-activation-card__icon">
            {isActive ? (
              <Power size={23} />
            ) : (
              <Lock size={23} />
            )}
          </div>
          <div>
            <p>
              {translateFallback(
                t,
                'menus.details.activation.eyebrow',
                'Activation',
              )}
            </p>
            <h2>
              {isActive
                ? translateFallback(
                    t,
                    'menus.details.activation.activeTitle',
                    'Menu is published',
                  )
                : translateFallback(
                    t,
                    'menus.details.activation.inactiveTitle',
                    'Menu is not published',
                  )}
            </h2>
            <small>
              {isActive
                ? translateFallback(
                    t,
                    'menus.details.activation.activeDescription',
                    'Customers can open this menu using the public link or QR code.',
                  )
                : translateFallback(
                    t,
                    'menus.details.activation.inactiveDescription',
                    'Activate this menu to generate the QR code and make the public link available.',
                  )}
            </small>
          </div>
          <button
            type="button"
            className={
              isActive
                ? 'menu-details-danger-button'
                : 'menu-details-success-button'
            }
            onClick={handleToggleStatus}
            disabled={loadingAction}
          >
            {loadingAction ? (
              <Loader2 size={17} />
            ) : isActive ? (
              <PowerOff size={17} />
            ) : (
              <Power size={17} />
            )}
            {isActive
              ? translateFallback(
                  t,
                  'menus.details.activation.deactivate',
                  'Deactivate menu',
                )
              : translateFallback(
                  t,
                  'menus.details.activation.activate',
                  'Activate menu',
                )}
          </button>
          {!isDefault && (
            <button
              type="button"
              className="menu-details-secondary-mini-button"
              onClick={handleSetDefault}
              disabled={loadingAction}
            >
              <Star size={16} />
              {translateFallback(
                t,
                'menus.details.default.set',
                'Set as default',
              )}
            </button>
          )}
        </article>
        <article
          className={`menu-details-activation-card ${
            orderingEnabled
              ? 'is-active'
              : 'is-inactive'
          }`}
        >
          <div className="menu-details-activation-card__icon">
            {orderingEnabled ? (
              <ShoppingBag size={23} />
            ) : (
              <Lock size={23} />
            )}
          </div>
          <div>
            <p>
              {translateFallback(
                t,
                'menus.details.ordering.eyebrow',
                'Online orders',
              )}
            </p>
            <h2>
              {orderingEnabled
                ? translateFallback(
                    t,
                    'menus.details.ordering.activeTitle',
                    'Online ordering is enabled',
                  )
                : translateFallback(
                    t,
                    'menus.details.ordering.inactiveTitle',
                    'Online ordering is disabled',
                  )}
            </h2>
            <small>
              {orderingEnabled
                ? translateFallback(
                    t,
                    'menus.details.ordering.activeDescription',
                    'Customers can add products to the cart and submit orders from the public menu.',
                  )
                : translateFallback(
                    t,
                    'menus.details.ordering.inactiveDescription',
                    'Customers can browse this menu only. Cart, checkout and customer order links are hidden.',
                  )}
            </small>
          </div>
          <button
            type="button"
            className={
              orderingEnabled
                ? 'menu-details-danger-button'
                : 'menu-details-success-button'
            }
            onClick={handleToggleOrdering}
            disabled={loadingAction}
          >
            {loadingAction ? (
              <Loader2 size={17} />
            ) : orderingEnabled ? (
              <PowerOff size={17} />
            ) : (
              <ShoppingBag size={17} />
            )}
            {orderingEnabled
              ? translateFallback(
                  t,
                  'menus.details.ordering.deactivate',
                  'Disable ordering',
                )
              : translateFallback(
                  t,
                  'menus.details.ordering.activate',
                  'Enable ordering',
                )}
          </button>
        </article>
        </div>
      </section>
      <section className="menu-details-stats-grid">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <article
              key={stat.key}
              className="menu-details-stat-card"
            >
              <span>
                <Icon size={21} />
              </span>
              <div>
                <strong>{stat.value}</strong>
                <p>{stat.label}</p>
              </div>
            </article>
          );
        })}
      </section>
      <div className="menu-details-qr-row">
      <section
        className={`menu-details-qr-panel ${
          isActive
            ? 'is-active'
            : 'is-locked'
        }`}
      >
        <div className="menu-details-qr-panel__content">
          <span className="menu-details-qr-panel__icon">
            {isActive ? (
              <QrCode size={26} />
            ) : (
              <Lock size={26} />
            )}
          </span>
          <div>
            <p>
              {translateFallback(
                t,
                'menus.details.share.eyebrow',
                'Menu sharing',
              )}
            </p>
            <h2>
              {isActive
                ? translateFallback(
                    t,
                    'menus.details.share.activeTitle',
                    'QR code ready',
                  )
                : translateFallback(
                    t,
                    'menus.details.share.lockedTitle',
                    'QR code available after activation',
                  )}
            </h2>
            <small>
              {isActive
                ? translateFallback(
                    t,
                    'menus.details.share.activeDescription',
                    'Print this QR code or share the public link with your customers.',
                  )
                : translateFallback(
                    t,
                    'menus.details.share.lockedDescription',
                    'Activate this menu first. The QR code and public link will appear here immediately after activation.',
                  )}
            </small>
          </div>
        </div>
        {isActive ? (
          <div className="menu-details-share-box">
            <strong>
              {activeMenu.name}
            </strong>
            <span>{publicUrl}</span>
            <div className="menu-details-share-actions">
              <button
                type="button"
                className="dashboard-secondary-button"
                onClick={handleCopyMenuLink}
                disabled={!publicUrl}
              >
                <Copy size={17} />
                {translateFallback(
                  t,
                  'menus.details.share.copy',
                  'Copy link',
                )}
              </button>
              <button
                type="button"
                className="dashboard-secondary-button"
                onClick={handleDownloadQrCode}
                disabled={!qrCodeDataUrl}
              >
                <Download size={17} />
                {translateFallback(
                  t,
                  'menus.details.share.download',
                  'Download QR',
                )}
              </button>
              {publicMenuPath && (
                <Link
                  to={publicMenuPath}
                  target="_blank"
                  rel="noreferrer"
                  className="dashboard-primary-button"
                >
                  <ExternalLink size={17} />
                  {translateFallback(
                    t,
                    'menus.details.share.open',
                    'Open menu',
                  )}
                </Link>
              )}
            </div>
          </div>
        ) : (
          <div className="menu-details-qr-locked">
            <span>
              <Lock size={56} />
            </span>
            <strong>
              {translateFallback(
                t,
                'menus.details.share.lockedQr',
                'Activate the menu to generate its QR code.',
              )}
            </strong>
            <button
              type="button"
              className="menu-details-success-button"
              onClick={handleToggleStatus}
              disabled={loadingAction}
            >
              {loadingAction ? (
                <Loader2 size={17} />
              ) : (
                <Power size={17} />
              )}
              {translateFallback(
                t,
                'menus.details.activation.activate',
                'Activate menu',
              )}
            </button>
          </div>
        )}
      </section>
      {isActive && (
        <section className="menu-details-qr-only-section">
          <div className="menu-details-qr-only-heading">
            <div>
              <p>
                {translateFallback(
                  t,
                  'menus.details.share.eyebrow',
                  'QR code',
                )}
              </p>
              <h2>
                {translateFallback(
                  t,
                  'menus.details.share.activeTitle',
                  'QR code ready',
                )}
              </h2>
            </div>
            <button
              type="button"
              className="dashboard-secondary-button"
              onClick={handleDownloadQrCode}
              disabled={!qrCodeDataUrl}
            >
              <Download size={17} />
              {translateFallback(
                t,
                'menus.details.share.download',
                'Download QR',
              )}
            </button>
          </div>
          <div className="menu-details-qr-only-body">
            <div className="menu-details-qr-only-frame">
              {qrCodeDataUrl ? (
                <img
                  src={qrCodeDataUrl}
                  alt={translateFallback(
                    t,
                    'menus.details.share.qrAlt',
                    'Menu QR code',
                  )}
                />
              ) : (
                <QrCode size={110} />
              )}
            </div>
          </div>
        </section>
      )}
      </div>
      <section className="menu-details-preview-section">
        <div className="menu-details-section-heading">
          <div>
            <p>
              {translateFallback(
                t,
                'menus.details.previewEyebrow',
                'Live preview',
              )}
            </p>
            <h2>
              {translateFallback(
                t,
                'menus.details.previewTitle',
                'Menu preview',
              )}
            </h2>
          </div>
          <div className="menu-details-preview-tools">
            <div className="menu-details-zoom-control">
              <button
                type="button"
                onClick={() => {
                  setIsPreviewZoomManual(true);
                  setPreviewZoom(
                    (current) =>
                      Math.max(
                        0.28,
                        Number(
                          (
                            current -
                            0.1
                          ).toFixed(2),
                        ),
                      ),
                  );
                }}
                aria-label={t('menus.details.previewControls.zoomOut')}
              >
                <ZoomOut size={15} />
              </button>
              <button
                type="button"
                className="menu-details-zoom-value"
                onClick={() => {
                  setIsPreviewZoomManual(false);
                  fitPreviewToCanvas();
                }}
                title={t('menus.details.previewControls.fitToScreen')}
              >
                {Math.round(
                  previewZoom * 100,
                )}%
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsPreviewZoomManual(true);
                  setPreviewZoom(
                    (current) =>
                      Math.min(
                        1.25,
                        Number(
                          (
                            current +
                            0.1
                          ).toFixed(2),
                        ),
                      ),
                  );
                }}
                aria-label={t('menus.details.previewControls.zoomIn')}
              >
                <ZoomIn size={15} />
              </button>
            </div>
            <Link
              to={`/menus/${activeMenu.id}/customize`}
              className="dashboard-secondary-button"
            >
              <Palette size={17} />
              {translateFallback(
                t,
                'menus.actions.customize',
                'Customize',
              )}
            </Link>
          </div>
        </div>
        <div
          ref={previewStageRef}
          className="menu-details-preview-stage"
          style={{
            '--preview-zoom':
              previewZoom,
            '--preview-scaled-width':
              `${Math.ceil(
                PREVIEW_PHONE_WIDTH *
                  previewZoom,
              )}px`,
            '--preview-scaled-height':
              `${Math.ceil(
                PREVIEW_PHONE_HEIGHT *
                  previewZoom,
              )}px`,
            '--custom-logo-size':
              `${previewDesignSettings.logo_size || 72}px`,
            '--custom-content-width':
              `${previewDesignSettings.content_width || 1100}px`,
            '--custom-section-gap':
              `${previewDesignSettings.section_gap || 24}px`,
            '--custom-heading-scale':
              (previewDesignSettings.heading_scale || 100) / 100,
            '--custom-body-scale':
              (previewDesignSettings.body_scale || 100) / 100,
            '--custom-surface':
              previewDesignSettings.surface_color ||
              '#ffffff',
            '--custom-accent':
              previewDesignSettings.accent_color ||
              '#D7E3E0',
          }}
        >
          <div className="menu-details-preview-zoom-frame">
            <div className="menu-details-preview-pan-surface">
              <div className="menu-details-preview-scale-layer">
                <div className="menu-details-public-phone">
                  <PublicMenuExperience
                    mode="preview"
                    phonePreview
                    menu={{
                      ...activeMenu,
                      design_settings:
                        previewDesignSettings,
                    }}
                    establishment={establishment}
                    categories={categories}
                    products={products}
                    featuredProducts={featuredProducts}
                    template={template}
                    designSettings={
                      previewDesignSettings
                    }
                    orderingEnabled={orderingEnabled}
                    language={
                      activeMenu.default_language ||
                      language
                    }
                    searchable
                    className="menu-details-live-preview"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="menu-details-management-grid">
        <Link
          to={`/menus/${activeMenu.id}/edit`}
          className="menu-details-management-card"
        >
          <Edit3 size={22} />
          <div>
            <strong>
              {translateFallback(
                t,
                'menus.actions.editDetails',
                'Edit details',
              )}
            </strong>
            <small>
              {translateFallback(
                t,
                'menus.details.management.editDescription',
                'Update name, language, image and activation settings.',
              )}
            </small>
          </div>
        </Link>
        <Link
          to={`/menus/${activeMenu.id}/customize`}
          className="menu-details-management-card"
        >
          <Palette size={22} />
          <div>
            <strong>
              {translateFallback(
                t,
                'menus.actions.customize',
                'Customize',
              )}
            </strong>
            <small>
              {translateFallback(
                t,
                'menus.details.management.customizeDescription',
                'Change template, colors, density and visible sections.',
              )}
            </small>
          </div>
        </Link>
        <Link
          to="/menu-analytics"
          className="menu-details-management-card"
        >
          <BarChart3 size={22} />
          <div>
            <strong>
              {translateFallback(
                t,
                'menus.details.management.analytics',
                'Analytics',
              )}
            </strong>
            <small>
              {translateFallback(
                t,
                'menus.details.management.analyticsDescription',
                'Review visits, engagement and performance for this menu.',
              )}
            </small>
          </div>
        </Link>
        {isActive && publicMenuPath && (
          <Link
            to={publicMenuPath}
            target="_blank"
            rel="noreferrer"
            className="menu-details-management-card"
          >
            <Eye size={22} />
            <div>
              <strong>
                {translateFallback(
                  t,
                  'menus.actions.publicPreview',
                  'Public preview',
                )}
              </strong>
              <small>
                {translateFallback(
                  t,
                  'menus.details.management.publicDescription',
                  'Open exactly what customers will see.',
                )}
              </small>
            </div>
          </Link>
        )}
      </section>
    </div>
  );
}
export default MenuDetails;
